// frontend/src/components/CommentSection.tsx
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useComments, CommentNode, MentionUser } from '../hooks/useComments';
import { useAuth } from '../contexts/AuthContext';
import { Modal } from './Modal';
import {
  MessageSquare, ThumbsUp, ThumbsDown, Reply, Edit3, Trash2,
  Send, ChevronDown, ChevronUp, LogIn, AtSign, X, CornerDownRight, User
} from 'lucide-react';

interface CommentSectionProps {
  projectPublicId: string;
  projectAuthorUsername?: string | null;
}

export function CommentSection({ projectPublicId, projectAuthorUsername }: CommentSectionProps) {
  const {
    commentTree,
    loading,
    totalCount,
    sortBy,
    setSortBy,
    addComment,
    updateComment,
    deleteComment,
    toggleUpvote,
    toggleDownvote,
    searchMentionUsers,
  } = useComments(projectPublicId);

  const { user, profile } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  const hasUsername = !!profile?.username;

  const handleSubmitComment = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const mentions = extractMentions(newComment);
    const { error } = await addComment(newComment, mentions);

    if (error) {
      setSubmitError(error);
    } else {
      setNewComment('');
    }
    setSubmitting(false);
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-theme-text-muted" />
          <h2 className="text-base font-semibold text-theme-text">
            Comments{totalCount > 0 ? ` (${totalCount})` : ''}
          </h2>
        </div>
        {totalCount > 1 && (
          <SortDropdown sortBy={sortBy} onChange={setSortBy} />
        )}
      </div>

      {/* New comment input */}
      {user ? (
        hasUsername ? (
          <div className="mb-6">
            <CommentInput
              value={newComment}
              onChange={setNewComment}
              onSubmit={handleSubmitComment}
              submitting={submitting}
              placeholder="Add a comment..."
              searchMentionUsers={searchMentionUsers}
              projectAuthorUsername={projectAuthorUsername}
            />
            {submitError && (
              <p className="mt-1 text-xs text-theme-error">{submitError}</p>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowUsernameModal(true)}
            className="flex items-center gap-2 mb-6 w-full px-4 py-3 rounded-xl border border-theme-border-muted bg-theme-surface text-sm text-theme-text-muted hover:bg-theme-surface-hover transition-colors text-left"
          >
            <User className="h-4 w-4 shrink-0" />
            Set a username to join the conversation...
          </button>
        )
      ) : (
        <Link
          to={`/login?returnTo=${encodeURIComponent(window.location.pathname)}`}
          className="flex items-center gap-2 mb-6 px-4 py-3 rounded-xl border border-theme-border-muted bg-theme-surface text-sm text-theme-text-muted hover:bg-theme-surface-hover transition-colors"
        >
          <LogIn className="h-4 w-4" />
          Sign in to join the conversation
        </Link>
      )}

      {/* Comments list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 border-2 border-theme-text-muted/30 border-t-theme-text-muted rounded-full animate-spin" />
        </div>
      ) : commentTree.length === 0 ? (
        <p className="text-center text-sm text-theme-text-muted py-8">
          No comments yet. Be the first!
        </p>
      ) : (
        <div className="space-y-1">
          {commentTree.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              depth={0}
              userId={user?.id || null}
              hasUsername={hasUsername}
              onUsernameRequired={() => setShowUsernameModal(true)}
              onReply={addComment}
              onEdit={updateComment}
              onDelete={deleteComment}
              onUpvote={toggleUpvote}
              onDownvote={toggleDownvote}
              searchMentionUsers={searchMentionUsers}
              projectAuthorUsername={projectAuthorUsername}
            />
          ))}
        </div>
      )}

      {/* Username Required Modal */}
      <Modal
        isOpen={showUsernameModal}
        onClose={() => setShowUsernameModal(false)}
        title="Username Required"
        size="sm"
      >
        <div className="text-center py-2">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-theme-primary/10 mb-4">
            <User className="h-7 w-7 text-theme-primary" />
          </div>
          <p className="text-sm text-theme-text-muted mb-6">
            You need to set a username before you can leave comments. You can set one in your profile on the dashboard.
          </p>
          <div className="flex flex-col gap-3">
            <Link
              to="/dashboard"
              className="btn btn-primary w-full"
              onClick={() => setShowUsernameModal(false)}
            >
              Go to Dashboard
            </Link>
            <button
              onClick={() => setShowUsernameModal(false)}
              className="btn btn-secondary w-full"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// --- Sort Dropdown ---

function SortDropdown({
  sortBy,
  onChange,
}: {
  sortBy: string;
  onChange: (v: 'newest' | 'oldest' | 'popular') => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const labels: Record<string, string> = { newest: 'Newest', oldest: 'Oldest', popular: 'Popular' };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-theme-text-muted hover:text-theme-text transition-colors px-2 py-1 rounded-lg hover:bg-theme-surface"
      >
        {labels[sortBy]}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-theme-surface border border-theme-border-muted rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
          {(['newest', 'oldest', 'popular'] as const).map(option => (
            <button
              key={option}
              onClick={() => { onChange(option); setOpen(false); }}
              className={`block w-full text-left px-3 py-1.5 text-xs transition-colors ${
                sortBy === option
                  ? 'text-theme-primary bg-theme-primary/5'
                  : 'text-theme-text-muted hover:bg-theme-surface-hover'
              }`}
            >
              {labels[option]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Comment Input with @mention ---

function CommentInput({
  value,
  onChange,
  onSubmit,
  submitting,
  placeholder,
  searchMentionUsers,
  projectAuthorUsername,
  onCancel,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  placeholder: string;
  searchMentionUsers: (term: string) => Promise<MentionUser[]>;
  projectAuthorUsername?: string | null;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionResults, setMentionResults] = useState<MentionUser[]>([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [showMentions, setShowMentions] = useState(false);
  const mentionSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    onChange(text);

    // Detect @mention trigger
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      setMentionIndex(0);
      setShowMentions(true);

      // Debounced search
      clearTimeout(mentionSearchTimeout.current);
      mentionSearchTimeout.current = setTimeout(async () => {
        if (query.length >= 1) {
          const results = await searchMentionUsers(query);
          setMentionResults(results);
        } else {
          setMentionResults([]);
        }
      }, 200);
    } else {
      setShowMentions(false);
      setMentionQuery(null);
    }
  };

  const insertMention = (mentionUser: MentionUser) => {
    const el = textareaRef.current;
    if (!el || mentionQuery === null) return;

    const cursorPos = el.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const textAfterCursor = value.slice(cursorPos);

    // Replace @query with @username
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const newText = textBeforeCursor.slice(0, atIndex) + '@' + mentionUser.username + ' ' + textAfterCursor;

    onChange(newText);
    setShowMentions(false);
    setMentionQuery(null);

    // Restore focus
    setTimeout(() => {
      if (el) {
        const newCursorPos = atIndex + 1 + mentionUser.username.length + 1;
        el.setSelectionRange(newCursorPos, newCursorPos);
        el.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(prev => (prev + 1) % mentionResults.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(prev => (prev - 1 + mentionResults.length) % mentionResults.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionResults[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowMentions(false);
        return;
      }
    }

    // Submit on Ctrl/Cmd + Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2 items-start">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            className="w-full resize-none rounded-xl border border-theme-border-muted bg-theme-bg px-3 py-2.5 text-sm text-theme-text placeholder:text-theme-text-muted/50 focus:outline-none focus:ring-1 focus:ring-theme-primary/40 focus:border-theme-primary/40 transition-colors"
          />
          {/* Mention autocomplete dropdown */}
          {showMentions && mentionResults.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-64 bg-theme-surface border border-theme-border-muted rounded-lg shadow-lg z-20 py-1 max-h-48 overflow-y-auto">
              {mentionResults.map((mu, i) => (
                <button
                  key={mu.id}
                  onClick={() => insertMention(mu)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors ${
                    i === mentionIndex ? 'bg-theme-primary/10 text-theme-primary' : 'text-theme-text hover:bg-theme-surface-hover'
                  }`}
                >
                  <AtSign className="h-3.5 w-3.5 shrink-0 text-theme-text-muted" />
                  <span className="font-medium truncate">{mu.username}</span>
                  {mu.display_name && mu.display_name !== mu.username && (
                    <span className="text-theme-text-muted text-xs truncate">({mu.display_name})</span>
                  )}
                  {projectAuthorUsername && mu.username === projectAuthorUsername && (
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-theme-primary/10 text-theme-primary shrink-0">author</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 rounded-lg text-theme-text-muted hover:bg-theme-surface-hover transition-colors"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onSubmit}
            disabled={!value.trim() || submitting}
            className="p-2 rounded-lg bg-theme-primary text-white hover:bg-theme-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Post comment (Ctrl+Enter)"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Single Comment ---

function CommentItem({
  comment,
  depth,
  userId,
  hasUsername,
  onUsernameRequired,
  onReply,
  onEdit,
  onDelete,
  onUpvote,
  onDownvote,
  searchMentionUsers,
  projectAuthorUsername,
}: {
  comment: CommentNode;
  depth: number;
  userId: string | null;
  hasUsername: boolean;
  onUsernameRequired: () => void;
  onReply: (content: string, mentions: { id: string; username: string }[], parentId: string | null) => Promise<{ error: string | null }>;
  onEdit: (commentId: string, content: string, mentions: { id: string; username: string }[]) => Promise<{ error: string | null }>;
  onDelete: (commentId: string) => Promise<{ error: string | null }>;
  onUpvote: (commentId: string) => Promise<{ error: string | null }>;
  onDownvote: (commentId: string) => Promise<{ error: string | null }>;
  searchMentionUsers: (term: string) => Promise<MentionUser[]>;
  projectAuthorUsername?: string | null;
}) {
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editText, setEditText] = useState(comment.content);
  const [submitting, setSubmitting] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isOwn = userId === comment.user_id;
  const isDeleted = !!comment.deleted_at;
  const hasUpvoted = userId ? comment.upvotes.includes(userId) : false;
  const hasDownvoted = userId ? comment.downvotes.includes(userId) : false;
  const score = comment.upvotes.length - comment.downvotes.length;
  const isAuthor = projectAuthorUsername && comment.author_username === projectAuthorUsername;
  const wasEdited = comment.updated_at !== comment.created_at && !isDeleted;

  const handleReply = async () => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    const mentions = extractMentions(replyText);
    const { error } = await onReply(replyText, mentions, comment.id);
    if (!error) {
      setReplyText('');
      setReplying(false);
    }
    setSubmitting(false);
  };

  const handleEdit = async () => {
    if (!editText.trim() || submitting) return;
    setSubmitting(true);
    const mentions = extractMentions(editText);
    const { error } = await onEdit(comment.id, editText, mentions);
    if (!error) {
      setEditing(false);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    await onDelete(comment.id);
    setSubmitting(false);
    setConfirmDelete(false);
  };

  const timeAgo = getTimeAgo(comment.created_at);

  return (
    <div className={depth > 0 ? 'ml-4 sm:ml-8 border-l-2 border-theme-border-muted/50 pl-3 sm:pl-4' : ''}>
      <div className="py-2.5 group">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          {/* Avatar circle */}
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-accent-400 to-cyan-500 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
            {(comment.author_display_name || comment.author_username || '?')[0].toUpperCase()}
          </div>
          <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
            {comment.author_username ? (
              <Link
                to={`/u/${comment.author_username}`}
                className="text-sm font-medium text-theme-text hover:text-theme-primary transition-colors truncate"
              >
                {comment.author_display_name || comment.author_username}
              </Link>
            ) : (
              <span className="text-sm font-medium text-theme-text truncate">
                {comment.author_display_name || 'Anonymous'}
              </span>
            )}
            {isAuthor && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-theme-primary/10 text-theme-primary font-medium shrink-0">
                Author
              </span>
            )}
            <span className="text-xs text-theme-text-muted shrink-0">{timeAgo}</span>
            {wasEdited && (
              <span className="text-[10px] text-theme-text-muted/60 shrink-0">(edited)</span>
            )}
          </div>
        </div>

        {/* Content */}
        {editing ? (
          <div className="ml-8 mt-1">
            <CommentInput
              value={editText}
              onChange={setEditText}
              onSubmit={handleEdit}
              submitting={submitting}
              placeholder="Edit your comment..."
              searchMentionUsers={searchMentionUsers}
              projectAuthorUsername={projectAuthorUsername}
              onCancel={() => { setEditing(false); setEditText(comment.content); }}
              autoFocus
            />
          </div>
        ) : (
          <div className="ml-8">
            <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${
              isDeleted ? 'text-theme-text-muted/50 italic' : 'text-theme-text'
            }`}>
              {renderContent(comment.content, comment.mentions)}
            </p>
          </div>
        )}

        {/* Actions */}
        {!isDeleted && !editing && (
          <div className="ml-8 mt-1.5 flex items-center gap-1 -ml-[-32px]" style={{ marginLeft: '32px' }}>
            {/* Votes */}
            <button
              onClick={() => onUpvote(comment.id)}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs transition-colors ${
                hasUpvoted
                  ? 'text-emerald-500 bg-emerald-500/10'
                  : 'text-theme-text-muted hover:text-emerald-500 hover:bg-emerald-500/5'
              }`}
              title="Upvote"
            >
              <ThumbsUp className="h-3.5 w-3.5" />
              {comment.upvotes.length > 0 && <span>{comment.upvotes.length}</span>}
            </button>
            <button
              onClick={() => onDownvote(comment.id)}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs transition-colors ${
                hasDownvoted
                  ? 'text-red-500 bg-red-500/10'
                  : 'text-theme-text-muted hover:text-red-500 hover:bg-red-500/5'
              }`}
              title="Downvote"
            >
              <ThumbsDown className="h-3.5 w-3.5" />
              {comment.downvotes.length > 0 && <span>{comment.downvotes.length}</span>}
            </button>

            {score !== 0 && (
              <span className={`text-[10px] px-1 ${
                score > 0 ? 'text-emerald-500' : 'text-red-400'
              }`}>
                {score > 0 ? '+' : ''}{score}
              </span>
            )}

            {/* Reply (only allow 1 level deep) */}
            {depth < 1 && userId && (
              <button
                onClick={() => {
                  if (!hasUsername) {
                    onUsernameRequired();
                    return;
                  }
                  setReplying(!replying);
                }}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-theme-text-muted hover:text-theme-primary hover:bg-theme-primary/5 transition-colors"
              >
                <Reply className="h-3.5 w-3.5" />
                Reply
              </button>
            )}

            {/* Edit / Delete own comments */}
            {isOwn && (
              <>
                <button
                  onClick={() => { setEditing(true); setEditText(comment.content); }}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-theme-text-muted hover:text-theme-text hover:bg-theme-surface-hover transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Edit3 className="h-3 w-3" />
                </button>
                {confirmDelete ? (
                  <span className="flex items-center gap-1 text-xs">
                    <button
                      onClick={handleDelete}
                      disabled={submitting}
                      className="text-red-500 hover:text-red-600 font-medium px-1"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="text-theme-text-muted hover:text-theme-text px-1"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-theme-text-muted hover:text-red-500 hover:bg-red-500/5 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Reply input */}
        {replying && (
          <div className="ml-8 mt-2">
            <div className="flex items-center gap-1 text-xs text-theme-text-muted mb-1">
              <CornerDownRight className="h-3 w-3" />
              Replying to {comment.author_display_name || comment.author_username || 'comment'}
            </div>
            <CommentInput
              value={replyText}
              onChange={setReplyText}
              onSubmit={handleReply}
              submitting={submitting}
              placeholder="Write a reply..."
              searchMentionUsers={searchMentionUsers}
              projectAuthorUsername={projectAuthorUsername}
              onCancel={() => { setReplying(false); setReplyText(''); }}
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Replies */}
      {comment.replies.length > 0 && (
        <div>
          {comment.replies.length > 2 && !showReplies ? (
            <button
              onClick={() => setShowReplies(true)}
              className="ml-8 flex items-center gap-1 text-xs text-theme-primary hover:text-theme-primary/80 transition-colors py-1"
            >
              <ChevronDown className="h-3 w-3" />
              Show {comment.replies.length} replies
            </button>
          ) : comment.replies.length > 2 && showReplies ? (
            <>
              {comment.replies.map(reply => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  depth={depth + 1}
                  userId={userId}
                  hasUsername={hasUsername}
                  onUsernameRequired={onUsernameRequired}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onUpvote={onUpvote}
                  onDownvote={onDownvote}
                  searchMentionUsers={searchMentionUsers}
                  projectAuthorUsername={projectAuthorUsername}
                />
              ))}
              <button
                onClick={() => setShowReplies(false)}
                className="ml-8 flex items-center gap-1 text-xs text-theme-text-muted hover:text-theme-text transition-colors py-1"
              >
                <ChevronUp className="h-3 w-3" />
                Collapse replies
              </button>
            </>
          ) : (
            comment.replies.map(reply => (
              <CommentItem
                key={reply.id}
                comment={reply}
                depth={depth + 1}
                userId={userId}
                hasUsername={hasUsername}
                onUsernameRequired={onUsernameRequired}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onUpvote={onUpvote}
                onDownvote={onDownvote}
                searchMentionUsers={searchMentionUsers}
                projectAuthorUsername={projectAuthorUsername}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// --- Helpers ---

function extractMentions(text: string): { id: string; username: string }[] {
  // Extract @username patterns - IDs will be resolved server-side if needed
  const matches = text.match(/@(\w+)/g);
  if (!matches) return [];
  const seen = new Set<string>();
  return matches
    .map(m => m.slice(1)) // remove @
    .filter(username => {
      if (seen.has(username)) return false;
      seen.add(username);
      return true;
    })
    .map(username => ({ id: '', username }));
}

function renderContent(
  content: string,
  mentions: { id: string; username: string }[] | string
): React.ReactNode {
  if (!content) return null;

  // mentions may arrive as a JSON string from Postgres
  const parsedMentions: { id: string; username: string }[] = Array.isArray(mentions)
    ? mentions
    : (() => { try { const m = JSON.parse(mentions); return Array.isArray(m) ? m : []; } catch { return []; } })();

  // Split on @username patterns and make them clickable
  const mentionUsernames = new Set(parsedMentions.map(m => m.username));
  const parts = content.split(/(@\w+)/g);

  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      const username = part.slice(1);
      if (mentionUsernames.has(username)) {
        return (
          <Link
            key={i}
            to={`/u/${username}`}
            className="text-theme-primary hover:underline font-medium"
          >
            {part}
          </Link>
        );
      }
      // Even non-matched @mentions get styled
      return (
        <span key={i} className="text-theme-primary font-medium">{part}</span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function getTimeAgo(dateString: string): string {
  const now = Date.now();
  const date = new Date(dateString).getTime();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + 'm ago';
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h ago';
  const days = Math.floor(hours / 24);
  if (days < 30) return days + 'd ago';
  const months = Math.floor(days / 30);
  if (months < 12) return months + 'mo ago';
  return Math.floor(months / 12) + 'y ago';
}